import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, Truck, PackageCheck, XCircle, MessageCircle, Ghost, Phone, RefreshCw, Search, KeyRound, FileText, Star, Gift, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, API, whatsappLink, snapchatLink, waNumber } from "../lib/api";
import { useI18n } from "../i18n";
import { LiveCourierMap } from "../components/LiveCourierMap";
import { OrderAmounts } from "../components/OrderAmounts";

const STEPS = [
  { key: "nouvelle", label: "tr.s1", icon: Clock },
  { key: "acceptee", label: "tr.s2", icon: CheckCircle2 },
  { key: "en_livraison", label: "tr.s3", icon: Truck },
  { key: "livree", label: "tr.s4", icon: PackageCheck },
];

const hhmm = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

export default function TrackOrder() {
  const { t } = useI18n();
  const { ref } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/ref/${encodeURIComponent(ref)}`);
      setOrder(data); setError("");
    } catch { setError(t("tr.notFound")); }
    finally { setLoading(false); }
  }, [ref, t]);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    if (order?.courier_arrived_at && order?.status === "en_livraison") {
      try { navigator.vibrate?.([200, 100, 200]); } catch (e) { console.error(e); }
    }
  }, [order?.courier_arrived_at, order?.status]);

  if (loading) return <div className="max-w-lg mx-auto px-4 py-20 text-center text-zinc-500">{t("tr.loading")}</div>;
  if (error) return (
    <div className="max-w-lg mx-auto px-4 py-20 text-center">
      <Search className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
      <p className="text-zinc-400 mb-6" data-testid="track-not-found">{error}</p>
      <Link to="/fidelite" className="inline-block bg-neon-green text-black font-anton uppercase px-6 py-3 glow-green">{t("tr.byPhone")}</Link>
    </div>
  );

  const refused = order.status === "refusee";
  const delivered = order.status === "livree";
  const activeIdx = order.status === "a_verifier" ? 2 : STEPS.findIndex((s) => s.key === order.status);
  const wa = whatsappLink(order.vendor_whatsapp || order.vendor_phone, `${t("tr.msg")} #${order.ref}`);
  const snap = snapchatLink(order.vendor_snapchat);
  const copySnapMsg = () => { Promise.resolve(navigator.clipboard?.writeText(`${t("tr.msg")} #${order.ref}`)).catch(console.error); };
  const tel = waNumber(order.vendor_phone || order.vendor_whatsapp);
  const times = {};
  (order.status_history || []).forEach((h) => { times[h.status] = h.at; });
  const courier = order.courier;
  const dest = order.customer_lat != null && order.customer_lng != null ? [order.customer_lat, order.customer_lng] : null;
  const inDelivery = order.status === "en_livraison";

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-10" data-testid="track-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-anton text-3xl uppercase text-white">{t("tr.title")} <span className="neon-text-green">#{order.ref}</span></h1>
        <button data-testid="track-refresh" onClick={load} className="text-zinc-400 hover:text-neon-green flex items-center gap-1 text-xs uppercase"><RefreshCw className="h-4 w-4" /> {t("tr.refresh")}</button>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
        <p className="text-zinc-500 text-sm">{t("tr.vendor")} : <span className="text-neon-magenta font-semibold">{order.vendor_name}</span> · {order.city}</p>
        {courier?.distance_km != null && (
          <span data-testid="courier-distance-badge" className="inline-flex items-center gap-1.5 border border-neon-green/60 bg-neon-green/10 text-neon-green text-xs font-bold uppercase px-2.5 py-1">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute h-full w-full rounded-full bg-neon-green opacity-75" /><span className="relative rounded-full h-2 w-2 bg-neon-green" /></span>
            {t("tr.live")} · {courier.distance_km < 1 ? `${Math.round(courier.distance_km * 1000)} m` : `${courier.distance_km.toFixed(1)} km`}
          </span>
        )}
      </div>

      {refused ? (
        <div className="bg-ink-surface border border-red-500/50 p-6 text-center mb-8" data-testid="track-status">
          <XCircle className="h-10 w-10 text-red-400 mx-auto mb-2" />
          <p className="text-red-400 font-anton uppercase text-lg">{t("tr.refused")}</p>
          <p className="text-zinc-500 text-sm mt-1">{t("tr.refusedTxt")}</p>
        </div>
      ) : (
        <>
        {inDelivery && order.courier_arrived_at && (
          <div data-testid="courier-arrived-banner" className="bg-neon-green text-black p-4 mb-6 flex items-center gap-3 border-2 border-neon-green shadow-[0_0_28px_rgba(157,255,0,0.5)] animate-pulse">
            <span className="text-3xl">🛵</span>
            <div>
              <p className="font-anton uppercase text-lg leading-tight">{t("tr.arrived")}</p>
              <p className="text-sm font-semibold">{t("tr.arrivedTxt")} <span className="font-anton tracking-[0.25em]">{order.delivery_code}</span></p>
            </div>
          </div>
        )}
        {order.status === "attente_paiement" && (
          <div data-testid="track-awaiting-payment" className="border border-neon-magenta/60 bg-neon-magenta/10 p-4 mb-6 text-sm">
            <p className="font-anton uppercase text-neon-magenta">🏦 En attente de ton virement</p>
            <p className="text-zinc-300 text-xs mt-1">Montant : <span className="font-mono text-white">{Number(order.total).toFixed(2)} €</span> · Libellé : <span className="font-mono text-white">{order.payment_ref || `STUFF-${order.ref}`}</span>. Dès réception, ta commande passe en « Acceptée » et ton colis part sous 24/48h.</p>
          </div>
        )}
        {order.status === "a_verifier" && <p data-testid="track-verify" className="border border-orange-400/60 bg-orange-400/10 text-orange-200 p-3 mb-6 text-sm">⏱️ {t("tr.verify")}</p>}
        {order.status === "annulee" && <p data-testid="track-cancelled" className="border border-red-500/60 bg-red-500/10 text-red-300 p-3 mb-6 text-sm">❌ Commande annulée{order.payment_ref ? " — virement non reçu dans le délai" : ""}.</p>}
        <div className="flex justify-between mb-8" data-testid="track-status">
          {STEPS.map((s, i) => {
            const done = i <= activeIdx;
            const Icon = s.icon;
            return (
              <div key={s.key} className="flex-1 flex flex-col items-center relative">
                {i > 0 && <div className={`absolute top-5 right-1/2 w-full h-0.5 ${i <= activeIdx ? "bg-neon-green" : "bg-zinc-800"}`} />}
                <div className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center border-2 ${done ? "bg-neon-green border-neon-green text-black" : "bg-ink border-zinc-700 text-zinc-600"}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <span className={`text-[10px] uppercase mt-2 text-center ${done ? "text-neon-green" : "text-zinc-600"}`}>{t(s.label)}</span>
                {times[s.key] && done && <span className="text-[10px] text-zinc-500 mt-0.5">{hhmm(times[s.key])}</span>}
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* Carte livreur en direct */}
      {inDelivery && (
        <div className="border border-zinc-800 mb-6 overflow-hidden" data-testid="track-map-section">
          {courier ? (
            <>
              <LiveCourierMap courier={[courier.lat, courier.lng]} dest={dest} />
              <div className="flex items-center justify-between bg-ink-surface px-4 py-2.5 text-xs">
                <span className="flex items-center gap-1.5 text-neon-green font-bold uppercase">
                  <span className="relative flex h-2 w-2"><span className="animate-ping absolute h-full w-full rounded-full bg-neon-green opacity-75" /><span className="relative rounded-full h-2 w-2 bg-neon-green" /></span>
                  {t("tr.live")}
                </span>
                {courier.distance_km != null && <span className="text-zinc-300">{courier.distance_km < 1 ? `${Math.round(courier.distance_km * 1000)} m` : `${courier.distance_km.toFixed(1)} km`} {t("tr.away")}</span>}
                {courier.at && <span className="text-zinc-500">{t("tr.maj")} {hhmm(courier.at)}</span>}
              </div>
            </>
          ) : (
            <div className="bg-ink-surface px-4 py-4 text-zinc-500 text-sm flex items-center gap-2" data-testid="track-no-gps">
              <Truck className="h-4 w-4 text-neon-magenta shrink-0" /> {t("tr.noGps")}
            </div>
          )}
        </div>
      )}

      {/* Code de réception sécurisé */}
      {order.delivery_code && !refused && (
        <div className={`flex items-center justify-between gap-4 border p-4 sm:p-5 mb-6 ${delivered ? "border-zinc-800 bg-ink-surface opacity-60" : "border-neon-green/50 bg-neon-green/5"}`} data-testid="delivery-code-block">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center ${delivered ? "bg-zinc-800 text-zinc-500" : "bg-neon-green/15 text-neon-green"}`}>
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <p className={`font-anton uppercase text-sm ${delivered ? "text-zinc-400" : "text-white"}`}>{t("tr.code")}</p>
              <p className="text-zinc-500 text-xs mt-0.5">{delivered ? "✓" : t("tr.codeTxt")}</p>
            </div>
          </div>
          <span data-testid="delivery-code-value" className={`font-anton text-3xl sm:text-4xl tracking-[0.3em] pl-2 ${delivered ? "text-zinc-600" : "neon-text-green"}`}>{order.delivery_code}</span>
        </div>
      )}

      <div className="bg-ink-surface border border-zinc-800 p-4 mb-6">
        {order.items.map((i, idx) => (
          <div key={`${i.product_id || i.name}-${i.price}-${idx}`} className="flex justify-between text-sm py-1 text-zinc-300">
            <span>{i.qty}x {i.name}</span><span>{(i.price * i.qty).toFixed(2)}€</span>
          </div>
        ))}
        {order.promo_gift && (
          <div data-testid="track-promo-gift" className="flex justify-between text-sm py-1 text-neon-green font-semibold">
            <span>🎁 {order.promo_gift.label} (code {order.promo_gift.code})</span><span>OFFERT</span>
          </div>
        )}
        {order.gift && (
          <div data-testid="track-gift" className="flex justify-between text-sm py-1 text-neon-magenta font-semibold">
            <span>🎁 1x {order.gift.name}</span><span>OFFERT</span>
          </div>
        )}
        <div className="border-t border-zinc-800 mt-2 pt-2">
          <OrderAmounts amounts={order.amounts} />
        </div>
        <p data-testid="track-payment-status" className="text-xs mt-2 font-bold text-zinc-500">
          {(order.payment_method || "").toLowerCase().includes("virement") ? `🏦 Paiement par virement instantané${order.paid_at ? " — reçu ✓" : ""}` : (order.payment_method || "").toLowerCase().includes("carte") ? "💳 Paiement à la remise : carte sans contact sur le terminal du livreur" : "💶 Paiement à la remise : espèces (appoint ou rendu de monnaie)"}
        </p>
        {delivered && (
          <a data-testid="track-invoice-btn" href={`${API}/orders/ref/${order.ref}/invoice`} target="_blank" rel="noopener noreferrer"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-neon-magenta text-neon-magenta font-anton uppercase py-3 hover:bg-neon-magenta hover:text-black transition-colors">
            <FileText className="h-4 w-4" /> {t("tr.invoice")}{order.invoice_number ? ` · ${order.invoice_number}` : ""}
          </a>
        )}
      </div>

      <p className="text-zinc-500 text-xs uppercase mb-2">{t("tr.contact")}</p>
      <div className="flex gap-2 flex-wrap">
        {wa && <a data-testid="track-whatsapp" href={wa} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 bg-neon-green text-black font-anton uppercase py-3"><MessageCircle className="h-4 w-4" /> WhatsApp</a>}
        {snap && <a data-testid="track-snapchat" href={snap} onClick={copySnapMsg} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FFFC00] text-black font-anton uppercase py-3"><Ghost className="h-4 w-4" /> Snapchat</a>}
        {tel && <a data-testid="track-call" href={`tel:${tel}`} className="flex-1 inline-flex items-center justify-center gap-2 border border-zinc-700 text-zinc-300 font-anton uppercase py-3 hover:border-neon-green"><Phone className="h-4 w-4" /> {t("tr.call")}</a>}
      </div>

      {delivered && <OrderRating order={order} onRated={(bonus) => setOrder((o) => ({ ...o, reviewed: true, review_bonus: bonus ? { ...bonus, used: false, expired: false } : o.review_bonus }))} />}

      <p className="text-zinc-600 text-xs mt-8 text-center">{t("tr.auto")}</p>
    </div>
  );
}

function BonusCard({ bonus }) {
  const { t } = useI18n();
  const copy = () => { Promise.resolve(navigator.clipboard?.writeText(bonus.code)).then(() => toast.success(t("tr.bonusCopied"))).catch(console.error); };
  const dead = bonus.used || bonus.expired;
  return (
    <div data-testid="review-bonus-card" className={`mt-3 border p-4 text-center ${dead ? "border-zinc-800 opacity-60" : "border-neon-magenta/70 bg-neon-magenta/10 shadow-[0_0_24px_rgba(228,20,196,0.25)]"}`}>
      <p className="font-anton uppercase text-neon-magenta flex items-center justify-center gap-2"><Gift className="h-4 w-4" /> {t("tr.bonusTitle")} {bonus.label}</p>
      <p className="text-zinc-400 text-xs mt-1">{dead ? (bonus.used ? t("tr.bonusUsed") : t("tr.bonusExpired")) : t("tr.bonusHint")}</p>
      <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
        <span data-testid="review-bonus-code" className={`font-anton text-2xl tracking-[0.2em] px-3 py-1 border ${dead ? "text-zinc-500 border-zinc-700 line-through" : "text-white border-neon-magenta"}`}>{bonus.code}</span>
        {!dead && <button type="button" data-testid="review-bonus-copy" onClick={copy} className="min-h-[44px] inline-flex items-center gap-1.5 text-xs uppercase font-bold px-3 bg-neon-magenta text-white"><Copy className="h-4 w-4" /> {t("tr.bonusCopy")}</button>}
      </div>
      {bonus.expires_at && !dead && <p className="text-zinc-500 text-[11px] mt-2">{t("tr.bonusUntil")} {new Date(bonus.expires_at).toLocaleDateString()}</p>}
    </div>
  );
}

function OrderRating({ order, onRated }) {
  const { t } = useI18n();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (window.location.hash === "#avis") setTimeout(() => document.getElementById("avis")?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
  }, []);
  if (order.reviewed) return (
    <div id="avis" className="mt-6">
      <p data-testid="track-rated" className="text-center text-neon-green text-sm font-bold">⭐ {t("tr.rated")}</p>
      {order.review_bonus && <BonusCard bonus={order.review_bonus} />}
    </div>
  );
  const submit = async () => {
    if (rating < 1) return;
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", { order_ref: order.ref, rating });
      toast.success(t("tr.rated"));
      onRated(data?.bonus || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <div id="avis" data-testid="track-rating" className="mt-6 border border-zinc-800 bg-ink-surface p-4 text-center scroll-mt-24">
      <p className="font-anton uppercase text-white">{t("tr.rate")}</p>
      <p className="text-zinc-500 text-xs mb-3">{t("tr.rateHint")}</p>
      {order.bonus_offer && <p data-testid="track-bonus-offer" className="inline-flex items-center gap-1.5 text-neon-magenta text-xs font-bold border border-neon-magenta/50 px-2.5 py-1 mb-3"><Gift className="h-3.5 w-3.5" /> {t("tr.bonusOffer")} {order.bonus_offer}</p>}
      <div className="flex justify-center gap-2 mb-3">
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} type="button" data-testid={`track-star-${s}`} onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)} onClick={() => setRating(s)} aria-label={`${s} / 5`}>
            <Star className={`h-8 w-8 ${(hover || rating) >= s ? "text-neon-green fill-neon-green" : "text-zinc-700"}`} />
          </button>
        ))}
      </div>
      <button data-testid="track-rate-submit" onClick={submit} disabled={busy || rating < 1} className="bg-neon-green text-black font-anton uppercase px-6 py-2 glow-green disabled:opacity-40">{t("store.rateBtn")}</button>
    </div>
  );
}
